import {
  PeopleIcon, FightIcon, GunIcon, FireIcon, BalaklavaIcon, BriefcaseIcon, LyingPersonIcon, HandsUpIcon, FallIcon,
} from '@vlabs/icons';
import validate from '@vlabs/shared/validators';

import type { ScoreField } from './popovers/video-analytics/VideoAnalyticsScoreFilterPopover';
import type { VideoAnalyticType } from './workspaceFiltersModel';

interface FacecoverScoreField extends ScoreField {
  key: string;
}

interface VideoAnalyticsFilterConfig {
  analyticType: VideoAnalyticType;
  icon: React.ComponentType;
  scoreField?: ScoreField;
  scoreFields?: FacecoverScoreField[];
  titleKey: string;
}

const percentValidation = {
  min: validate.gte(0),
  max: validate.lte(100),
  validate: validate.number(),
};

const VIDEO_ANALYTICS_FILTERS: VideoAnalyticsFilterConfig[] = [
  {
    analyticType: 'crowd',
    titleKey: 'videoAnalytics:event_type.crowd.label',
    scoreField: {
      id: 'videoAnalytics.crowd.people_count',
      filterName: 'event^people_count__gte:integer',
      labelKey: 'videoAnalytics:people_count.label',
      validate: { validate: validate.integer() },
    },
    icon: PeopleIcon,
  },
  {
    analyticType: 'fight',
    titleKey: 'videoAnalytics:event_type.fight.label',
    scoreField: {
      id: 'videoAnalytics.fight.fight_score',
      filterName: 'event^overview^score__gte:numeric',
      labelKey: 'videoAnalytics:fight_score.label %',
      validate: percentValidation,
    },
    icon: FightIcon,
  },
  {
    analyticType: 'weapon',
    titleKey: 'videoAnalytics:event_type.weapon.label',
    scoreField: {
      id: 'videoAnalytics.weapon.weapon_score',
      filterName: 'event^weapon_score__gte:numeric',
      labelKey: 'videoAnalytics:weapon_score.label %',
      validate: percentValidation,
    },
    icon: GunIcon,
  },
  {
    analyticType: 'fall',
    titleKey: 'videoAnalytics:event_type.fall.label',
    scoreField: {
      id: 'videoAnalytics.fall.fall_score',
      filterName: 'event^fall_score__gte:numeric',
      labelKey: 'videoAnalytics:fall_score.label %',
      validate: percentValidation,
    },
    icon: FallIcon,
  },
  {
    analyticType: 'fire',
    titleKey: 'videoAnalytics:event_type.fire.label',
    scoreField: {
      id: 'videoAnalytics.fire.fire_score',
      filterName: 'event^overview^fire^fireScore__gte:numeric',
      labelKey: 'videoAnalytics:fire_score.label %',
      validate: percentValidation,
    },
    icon: FireIcon,
  },
  {
    analyticType: 'facecover',
    titleKey: 'videoAnalytics:event_type.facecover.label',
    icon: BalaklavaIcon,
    scoreField: {
      id: 'videoAnalytics.facecover.facecover_score',
      filterName: 'event^facecover_score__gte:numeric',
      labelKey: 'videoAnalytics:facecover_score.label %',
      validate: percentValidation,
    },
    scoreFields: [
      {
        key: 'niqab',
        id: 'videoAnalytics.facecover.niqab_score',
        filterName: 'event^niqab_score__gte:numeric',
        labelKey: 'videoAnalytics:niqab_score.label %',
        validate: percentValidation,
      },
      {
        key: 'balaclava',
        id: 'videoAnalytics.facecover.balaclava_score',
        filterName: 'event^balaclava_score__gte:numeric',
        labelKey: 'videoAnalytics:balaclava_score.label %',
        validate: percentValidation,
      },
      {
        key: 'other',
        id: 'videoAnalytics.facecover.other_score',
        filterName: 'event^other_score__gte:numeric',
        labelKey: 'videoAnalytics:other_score.label %',
        validate: percentValidation,
      },
    ],
  },
  {
    analyticType: 'lying',
    titleKey: 'videoAnalytics:event_type.lying.label',
    scoreField: {
      id: 'videoAnalytics.lying.lying_score',
      filterName: 'event^lying_score__gte:numeric',
      labelKey: 'videoAnalytics:lying_score.label %',
      validate: percentValidation,
    },
    icon: LyingPersonIcon,
  },
  {
    analyticType: 'handsup',
    titleKey: 'videoAnalytics:event_type.handsup.label',
    scoreField: {
      id: 'videoAnalytics.handsup.handsup_score',
      filterName: 'event^handsup_score__gte:numeric',
      labelKey: 'videoAnalytics:handsup_score.label %',
      validate: percentValidation,
    },
    icon: HandsUpIcon,
  },
  {
    analyticType: 'bag',
    titleKey: 'videoAnalytics:event_type.bag.label',
    icon: BriefcaseIcon,
  },
];

export { VIDEO_ANALYTICS_FILTERS };
export type { FacecoverScoreField, VideoAnalyticsFilterConfig };
